import { STATE_TAX_RATES, STATE_TAX_YEAR } from '../constants/stateTaxRates';
import { STATE_RETIREMENT_RULES } from '../constants/stateRetirementRules';
import { STANDARD_DEDUCTION } from '../constants/taxConstants';
import { formatPercent } from '../formatters';

const SENIOR_AGE = 65;
const isJoint = (filingStatus) => filingStatus === 'married_filing_jointly' || filingStatus === 'married_filing_separately';

/** States to choose from, by name: [code, name, hasIncomeTax]. */
export const STATE_CHOICES = Object.keys(STATE_TAX_RATES)
  .map((code) => [code, STATE_TAX_RATES[code].name, !STATE_TAX_RATES[code].none])
  .sort((a, b) => a[1].localeCompare(b[1]));

export const stateName = (code) => STATE_TAX_RATES[code]?.name || '';

/** True for a state whose brackets and allowances are fixed in dollars, not raised with prices. */
export const stateBracketsFixed = (code) => STATE_RETIREMENT_RULES[code]?.bracketsIndexed === false;

/** The county income tax a state adds on top ({ min, max, asOf, what }), or null. */
export const localIncomeTaxOf = (code) => STATE_RETIREMENT_RULES[code]?.localIncomeTax || null;


/** What the estimate for a state applies and leaves out, in words. Null without a state. */
export function stateTaxNotes(code, localRate = 0) {
  const rates = STATE_TAX_RATES[code];
  if (!rates) return null;
  if (rates.none) return { name: rates.name, none: true, applied: `${rates.name} has no income tax.`, leftOut: '', basis: '' };
  const rules = STATE_RETIREMENT_RULES[code] || {};
  const local = rules.localIncomeTax;
  const localApplied = local && localRate > 0 ? ` County income tax of ${formatPercent(localRate, 2)} is added, on the same taxable income.` : '';
  const leftParts = [];
  if (local && !(localRate > 0)) {
    leftParts.push(`${local.what}, at ${formatPercent(local.min, 2)} to ${formatPercent(local.max, 2)} (${local.asOf}); enter your county's rate on Profile to include it`);
  }
  if (rules.leftOut) leftParts.push(rules.leftOut.replace(/\.$/, ''));
  return {
    name: rates.name,
    none: false,
    applied: `${rules.applied || ''}${rules.bracketsIndexed === false ? ' Its brackets and allowances are fixed in dollars and are not raised with prices, so they are worth a little less each year of the plan.' : ''}${localApplied}`.trim(),
    leftOut: leftParts.length ? `${leftParts.join('; and ')}.` : '',
    basis: rules.basis || '',
    year: STATE_TAX_YEAR,
  };
}

function bracketTax(taxable, brackets) {
  let tax = 0;
  for (let i = 0; i < brackets.length; i++) {
    const [rate, over] = brackets[i];
    if (taxable <= over) break;
    const top = i + 1 < brackets.length ? brackets[i + 1][1] : Infinity;
    tax += (Math.min(taxable, top) - over) * rate;
  }
  return tax;
}

// An amount that shrinks in a straight line from `start` to `end` of income,
// never below `floor`. With start equal to end it is lost all at once.
function phased(amount, income, phase, m) {
  if (!phase || income <= phase.start[m]) return amount;
  const floor = phase.floor ? phase.floor[m] : 0;
  if (income >= phase.end[m]) return floor;
  return amount - (amount - floor) * (income - phase.start[m]) / (phase.end[m] - phase.start[m]);
}

const each = (amount, m) => (Array.isArray(amount) ? amount[m] : amount);

// ── Pension and retirement-account income left out of tax ──────────────────

function retirementExclusion(r, ctx) {
  const { ages, m, pension, plan, ira, gains, ss, agi, taxableSS } = ctx;
  let fromRetirement = 0;
  let pool = pension + plan;
  if (r.pensionExempt) {
    fromRetirement += pension;
    pool = plan;
  }
  if (r.noIra) pool -= ira;

  const eligible = ages.filter((a) => a >= (r.minAge || 0)).length;
  let cap = 0;
  if (eligible > 0) {
    if (r.all) cap = Infinity;
    else if (r.perReturn) cap = r.perReturn[m];
    else if (r.cap != null) {
      const older = r.capFromAge ? ages.filter((a) => a >= r.capFromAge.age).length : 0;
      cap = (eligible - older) * r.cap + older * (r.capFromAge ? r.capFromAge.cap : 0);
    }
    if (r.lessSocialSecurity) cap = Math.max(0, cap - ss);
    if (r.incomeLimit && Number.isFinite(cap)) {
      const measured = r.incomeLessSS ? agi - taxableSS : agi;
      cap = phased(cap, measured, { start: r.incomeLimit, end: r.incomeLimitEnd || r.incomeLimit }, m);
    }
  }
  let share = 1;
  if (r.steps) {
    // The share allowed falls in steps as income rises.
    share = 0;
    for (const [below, allowed] of r.steps) {
      if (agi < below[m]) { share = allowed; break; }
    }
  }
  const taken = Math.min(pool * share, cap);
  fromRetirement += taken;
  const fromGains = r.coversGains ? Math.min(gains, Math.max(0, cap - taken)) : 0;
  return { fromRetirement, fromGains };
}

// Colorado: one allowance per person covers Social Security first, then
// pension and account income. A couple is taken to share both equally.
function colorado({ ages, m, pension, plan, taxableSS, agi }) {
  const people = ages.length;
  const ssEach = taxableSS / people;
  const retirementEach = (pension + plan) / people;
  let ssTaxed = 0;
  let fromRetirement = 0;
  for (const age of ages) {
    if (age >= 65) {
      fromRetirement += Math.min(retirementEach, Math.max(0, 24000 - ssEach));
    } else if (age >= 55) {
      if (agi <= [75000, 95000][m]) {
        fromRetirement += Math.min(retirementEach, Math.max(0, 20000 - ssEach));
      } else {
        const ssOut = Math.min(ssEach, 20000);
        ssTaxed += ssEach - ssOut;
        fromRetirement += Math.min(retirementEach, 20000 - ssOut);
      }
    } else {
      ssTaxed += ssEach;
    }
  }
  return { ssTaxed, fromRetirement, fromGains: 0 };
}

// New Jersey: from 62, an exclusion per return that depends on total income
// (which leaves Social Security out).
function newJersey({ ages, m, pension, plan, ordinary, gains }) {
  if (!ages.some((a) => a >= 62)) return { ssTaxed: 0, fromRetirement: 0, fromGains: 0 };
  const income = ordinary + gains;
  const pool = pension + plan;
  let fromRetirement = 0;
  if (income <= 100000) fromRetirement = Math.min(pool, [75000, 100000][m]);
  else if (income <= 125000) fromRetirement = pool * [0.375, 0.5][m];
  else if (income <= 150000) fromRetirement = pool * [0.1875, 0.25][m];
  return { ssTaxed: 0, fromRetirement, fromGains: 0 };
}

const CUSTOM = { CO: colorado, NJ: newJersey };

// ── Social Security ─────────────────────────────────────────────────────────

function socialSecurityTaxed(rule, { ages, m, ss, taxableSS, agi }) {
  if (!rule || !(taxableSS > 0)) return 0;
  if (rule.exemptFromAge && ages.every((a) => a >= rule.exemptFromAge)) return 0;
  let share = 1;
  if (rule.exemptBelow && (!rule.needsAge || ages.every((a) => a >= rule.needsAge))) {
    if (agi <= rule.exemptBelow[m]) share = 0;
    else if (rule.phaseTo && agi < rule.phaseTo[m]) share = (agi - rule.exemptBelow[m]) / (rule.phaseTo[m] - rule.exemptBelow[m]);
  }
  let taxed = taxableSS * share;
  if (rule.capShareOfBenefits) taxed = Math.min(taxed, rule.capShareOfBenefits * ss);
  return taxed;
}

/**
 * State income tax for one year, as an estimate: the state's brackets,
 * standard deduction and personal exemption (stateTaxRates.js, generated),
 * and its main rules for retirees (stateRetirementRules.js): whether Social
 * Security is taxed, how much pension and retirement-account income is left
 * out, what people of 65 and over get on top, any special treatment of
 * long-term gains, and a deduction for federal tax where one exists.
 *
 *   ordinary    all ordinary income other than Social Security
 *   pension     the part of it that is an employer pension
 *   plan        the part of it withdrawn or converted from traditional accounts;
 *               iraShare: how much of that is IRA money and not an employer plan
 *   ss          Social Security received; taxableSS: the part taxed federally
 *   agi         federal adjusted gross income; federalTax: federal income tax
 *
 * Married filing separately is worked as one joint return, and a couple's
 * per-person allowances are added together. `localRate` is a county income
 * tax on the same taxable income, for states that have one (`localIncomeTax`:
 * Maryland, Indiana). Not modelled: other city and county income taxes, and
 * what each state lists under `leftOut`.
 */
export function stateIncomeTax({
  state, filingStatus, age, spouseAge, ordinary = 0, pension = 0, plan = 0, iraShare = 0,
  ss = 0, taxableSS = 0, gains = 0, agi = 0, federalTax = 0, localRate = 0,
}) {
  const rates = STATE_TAX_RATES[state];
  if (!rates || rates.none) return 0;
  const rules = STATE_RETIREMENT_RULES[state] || {};
  const joint = isJoint(filingStatus);
  const m = joint ? 1 : 0;
  const brackets = (rules.rates || rates)[joint ? 'married' : 'single'];
  const ages = joint && spouseAge != null ? [age, spouseAge] : [age];
  const seniors = ages.filter((a) => a >= SENIOR_AGE).length;

  if (rates.gainsOnly) {
    // Washington: an excise on long-term gains above a large yearly deduction.
    return bracketTax(Math.max(0, gains - rates.deduction[m]), brackets);
  }

  const pensionPart = Math.min(Math.max(0, pension), ordinary);
  const planPart = Math.min(Math.max(0, plan), ordinary - pensionPart);
  const ctx = { ages, m, ordinary, pension: pensionPart, plan: planPart, ira: planPart * iraShare, gains, ss, taxableSS, agi };

  let ssTaxed;
  let excluded;
  if (rules.custom) {
    const result = CUSTOM[rules.custom](ctx);
    ssTaxed = result.ssTaxed;
    excluded = result;
  } else {
    ssTaxed = socialSecurityTaxed(rules.socialSecurity, ctx);
    excluded = rules.retirement ? retirementExclusion(rules.retirement, ctx) : { fromRetirement: 0, fromGains: 0 };
  }

  const gainsTaxed = Math.max(0, (gains - excluded.fromGains) * (1 - (rules.gainsExclusion || 0)) - (rules.gainsExclusionFlat || 0));
  const ordinaryIncome = Math.max(0, ordinary - excluded.fromRetirement) + ssTaxed;

  const baseDeduction = rules.deduction || rates.deduction;
  let deduction = phased(rates.deductionIsCredit ? 0 : baseDeduction[m], agi, rules.deductionPhase, m);
  if (rules.deductionEachSpouse && joint) deduction *= 2;
  if (!rates.exemptionIsCredit) deduction += phased(rates.exemption[m], agi, rules.exemptionPhase, m);
  if (rules.federalSeniorDeduction) deduction += seniors * STANDARD_DEDUCTION[joint ? 'married_filing_jointly' : 'single'].additional;
  if (seniors > 0) {
    const senior = rules.seniorDeduction;
    if (senior) {
      let amount = seniors * each(senior.amount, m);
      if (senior.phase) amount = phased(amount, agi, senior.phase, m);
      if (senior.lessIncomeOver) amount = Math.max(0, amount - Math.max(0, agi - taxableSS - senior.lessIncomeOver[m]));
      if (senior.lessTaxableSS) amount = Math.max(0, amount - taxableSS);
      deduction += amount;
    }
    if (rules.seniorExemption) deduction += seniors * rules.seniorExemption;
  }
  const federal = rules.federalTaxDeduction;
  if (federal && federalTax > 0) {
    let amount = federalTax;
    if (federal.shares) {
      const tier = federal.shares.find(([upTo]) => agi <= upTo);
      amount *= tier ? tier[1] : 0;
    }
    if (federal.cap) amount = Math.min(amount, federal.cap[m]);
    if (federal.phase) amount = phased(amount, agi, federal.phase, m);
    deduction += amount;
  }

  // The deduction comes off ordinary income first; what is left of it shelters gains.
  const ordinaryTaxable = Math.max(0, ordinaryIncome - deduction);
  const gainsTaxable = Math.max(0, gainsTaxed - Math.max(0, deduction - ordinaryIncome));
  const taxable = ordinaryTaxable + gainsTaxable;

  let tax = bracketTax(taxable, brackets);
  if (rules.gainsRates && gainsTaxable > 0) {
    // Long-term gains at their own two rates, stacked on top of ordinary income.
    const threshold = brackets.length > 1 ? brackets[1][1] : Infinity;
    const low = Math.min(gainsTaxable, Math.max(0, threshold - ordinaryTaxable));
    tax = bracketTax(ordinaryTaxable, brackets) + low * rules.gainsRates.under + (gainsTaxable - low) * rules.gainsRates.over;
  } else if (rules.gainsMaxRate != null && gainsTaxable > 0) {
    tax = Math.min(tax, bracketTax(ordinaryTaxable, brackets) + gainsTaxable * rules.gainsMaxRate);
  }

  let credits = 0;
  if (rates.exemptionIsCredit) credits += phased(rates.exemption[m], agi, rules.exemptionPhase, m);
  if (rates.deductionIsCredit) {
    const phase = rules.deductionCreditPhase;
    credits += phase ? Math.max(0, rates.deduction[m] - phase.rate * Math.max(0, taxable - phase.start[m])) : rates.deduction[m];
  }
  if (rules.seniorCredit) {
    credits += ages.filter((a) => a >= (rules.seniorCredit.age || SENIOR_AGE)).length * rules.seniorCredit.amount;
  }
  for (const credit of rules.returnCredits || []) {
    if (credit.minAge && !ages.some((a) => a >= credit.minAge)) continue;
    if (credit.retirementOver != null && !(pensionPart + planPart > credit.retirementOver)) continue;
    if (credit.incomeBelow != null && agi >= credit.incomeBelow) continue;
    credits += credit.amount;
  }
  const ssCredit = rules.socialSecurity?.credit;
  if (ssCredit && ssTaxed > 0) {
    // Utah: a credit for the tax on benefits, shrinking as income rises.
    const rate = brackets[brackets.length - 1][0];
    credits += Math.max(0, rate * ssTaxed - ssCredit.rate * Math.max(0, agi - ssCredit.start[m]));
  }
  const localTax = rules.localIncomeTax && localRate > 0 ? localRate * taxable : 0;
  return Math.max(0, tax - credits) + localTax;
}
